import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Star, 
  MapPin, 
  Clock, 
  Phone, 
  Globe, 
  Heart, 
  Share2,
  ChevronLeft,
  ChevronRight,
  Check,
  Calendar as CalendarIcon,
  X,
  Building2,
  Loader2,
  Mail,
  Package
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { cn, getTimezoneOffset } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { AdSenseUnit } from "@/components/ads/AdSenseUnit";
import { format as formatDate } from "date-fns";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateLocalBusinessSchema } from "@/components/seo/StructuredData";
import { BookingConsentModal } from "@/components/terms/BookingConsentModal";

interface Business {
  id: string;
  business_name: string;
  category: string;
  address: string;
  state?: string | null;
  postcode?: string | null;
  description: string | null;
  contact_number?: string | null;
  email?: string | null;
  website: string | null;
  business_logo: string | null;
  operating_hours: any;
  timezone?: string | null;
}

interface InventoryItem {
  id: string;
  product_name: string;
  description: string | null;
  price: number;
  stock_qty: number;
  product_photo: string | null;
}

interface BusinessPhoto {
  id: string;
  photo_url: string;
  display_order: number;
}

interface Service {
  id: string;
  title: string;
  description: string | null;
  duration_minutes: number;
  price: number;
  tax_rate: number;
  category: string | null;
}

interface Practitioner {
  id: string;
  name: string;
  specialization: string | null;
  photo: string | null;
  availability_schedule: any;
}

export default function ProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [photos, setPhotos] = useState<BusinessPhoto[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [existingBookings, setExistingBookings] = useState<any[]>([]);
  const [reviews, setReviews] = useState<{ rating: number; comment: string | null; created_at: string; profiles?: { name: string } }[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState<string | null>(null);
  const [showBookingSheet, setShowBookingSheet] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingConsentModal, setShowBookingConsentModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBusinessData();
    }
  }, [id]);

  // Real-time subscription for booking updates - ensures other users see newly booked slots immediately
  useEffect(() => {
    if (!id) return;
    
    console.log("[ProviderDetail] Setting up real-time booking subscription for business:", id);
    
    const channel = supabase
      .channel(`booking-updates-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `business_id=eq.${id}`
        },
        (payload) => {
          console.log('[ProviderDetail] Real-time booking change detected:', payload);
          // Refresh bookings data when any booking changes
          if (selectedDate) {
            // Re-fetch bookings for the selected date
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            const startBuffer = new Date(startOfDay.getTime() - 24 * 60 * 60 * 1000);
            
            const endOfDay = new Date(selectedDate);
            endOfDay.setHours(23, 59, 59, 999);
            const endBuffer = new Date(endOfDay.getTime() + 24 * 60 * 60 * 1000);
            
            supabase
              .from("bookings")
              .select("practitioner_id, booking_date_time, status, services(duration_minutes)")
              .eq("business_id", id)
              .gte("booking_date_time", startBuffer.toISOString())
              .lte("booking_date_time", endBuffer.toISOString())
              .neq("status", "cancelled")
              .then(({ data }) => {
                if (data) {
                  console.log('[ProviderDetail] Updated bookings from real-time event:', data);
                  setExistingBookings(data);
                }
              });
          }
        }
      )
      .subscribe((status) => {
        console.log('[ProviderDetail] Realtime subscription status:', status);
      });

    return () => {
      console.log('[ProviderDetail] Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [id, selectedDate]);

  // Refetch bookings when date or practitioner changes to ensure fresh availability data
  useEffect(() => {
    const fetchBookingsForDate = async () => {
      if (!selectedDate || !id) return;
      
      // Widen the query range by 24 hours on each side to account for timezone differences
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const startBuffer = new Date(startOfDay.getTime() - 24 * 60 * 60 * 1000);
      
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      const endBuffer = new Date(endOfDay.getTime() + 24 * 60 * 60 * 1000);
      
      console.log("[ProviderDetail] Fetching bookings for date range:", {
        selectedDate: selectedDate.toISOString(),
        queryStart: startBuffer.toISOString(),
        queryEnd: endBuffer.toISOString()
      });
      
      const { data } = await supabase
        .from("bookings")
        .select("practitioner_id, booking_date_time, status, services(duration_minutes)")
        .eq("business_id", id)
        .gte("booking_date_time", startBuffer.toISOString())
        .lte("booking_date_time", endBuffer.toISOString())
        .neq("status", "cancelled");
      
      console.log("[ProviderDetail] Fetched bookings:", data);
      
      if (data) {
        setExistingBookings(data);
      }
    };
    
    fetchBookingsForDate();
  }, [selectedDate, selectedPractitioner, id]);

  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      // Fetch business details from public view (excludes sensitive data like TIN, payment IDs)
      const { data: businessData, error: businessError } = await supabase
        .from("businesses_public")
        .select("*")
        .eq("id", id)
        .single();

      if (businessError) throw businessError;
      setBusiness(businessData as Business);

      // Fetch business photos
      const { data: photosData } = await supabase
        .from("business_photos")
        .select("*")
        .eq("business_id", id)
        .order("display_order", { ascending: true });

      setPhotos(photosData || []);

      // Fetch services - sorted by category
      const { data: servicesData } = await supabase
        .from("services")
        .select("id, title, description, duration_minutes, price, tax_rate, category")
        .eq("business_id", id)
        .eq("is_active", true)
        .order("category", { ascending: true })
        .order("title", { ascending: true });

      setServices(servicesData || []);

      // Fetch practitioners (using public view - excludes sensitive contact info)
      const { data: practitionersData, error: practError } = await supabase
        .from("practitioners_public")
        .select("id, name, specialization, photo, availability_schedule, is_active")
        .eq("business_id", id)
        .eq("is_active", true);

      if (practError) {
        console.error("Error fetching practitioners:", practError);
      }
      setPractitioners(practitionersData || []);

      // Fetch inventory
      const { data: inventoryData } = await supabase
        .from("inventory")
        .select("id, product_name, description, price, stock_qty, product_photo")
        .eq("business_id", id)
        .eq("is_active", true)
        .gt("stock_qty", 0);

      setInventory(inventoryData || []);

      // Fetch existing bookings for conflict checking
      const { data: bookingsData } = await supabase
        .from("bookings")
        .select("practitioner_id, booking_date_time, status, services(duration_minutes)")
        .eq("business_id", id)
        .neq("status", "cancelled");

      setExistingBookings(bookingsData || []);

      // Fetch reviews for average rating with comments and customer names
      const { data: reviewsData } = await supabase
        .from("reviews")
        .select("rating, comment, created_at, profiles:customer_id(name)")
        .eq("business_id", id)
        .order("created_at", { ascending: false });

      setReviews(reviewsData?.map(r => ({
        rating: r.rating,
        comment: r.comment,
        created_at: r.created_at,
        profiles: r.profiles as { name: string } | undefined
      })) || []);
    } catch (error) {
      console.error("Error fetching business:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCategory = (category: string) => {
    return category
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getImages = () => {
    if (photos.length > 0) {
      return photos.map(p => p.photo_url);
    }
    if (business?.business_logo) {
      return [business.business_logo];
    }
    return [];
  };

  const images = getImages();

  const nextImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  // Check if practitioner is working on a specific day
  const isPractitionerWorkingOnDay = (practitioner: Practitioner, date: Date) => {
    if (!practitioner.availability_schedule) return false;
    const dayName = format(date, "EEEE"); // e.g., "Monday"
    const daySchedule = practitioner.availability_schedule[dayName];
    return daySchedule?.working === true;
  };

  // Get practitioners available today for display
  const getTodayAvailablePractitioners = () => {
    const today = new Date();
    return practitioners.filter(p => isPractitionerWorkingOnDay(p, today));
  };

  // Get practitioners available on selected date for booking
  const getAvailablePractitionersForDate = (date: Date) => {
    return practitioners.filter(p => isPractitionerWorkingOnDay(p, date));
  };

  // Check if a practitioner is available at a specific time slot
  const isPractitionerAvailableAtTime = (practitionerId: string, date: Date, timeStr: string) => {
    if (selectedService === null) return true;
    
    const service = services[selectedService];
    const serviceDuration = service?.duration_minutes || 60;
    
    // Parse the time string
    const [time, period] = timeStr.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    let hour = hours;
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    
    // Create slot times using local time
    const slotStart = new Date(date);
    slotStart.setHours(hour, minutes, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + serviceDuration * 60000);
    
    // Get the date components of the slot for same-day comparison
    const slotDateStr = `${slotStart.getFullYear()}-${String(slotStart.getMonth() + 1).padStart(2, '0')}-${String(slotStart.getDate()).padStart(2, '0')}`;
    
    // Check for conflicts with existing bookings
    for (const booking of existingBookings) {
      if (booking.practitioner_id !== practitionerId) continue;
      if (booking.status === "cancelled") continue;
      
      // Parse booking time - handle both UTC and local timezone formats
      const bookingDateStr = booking.booking_date_time;
      const bookingStart = new Date(bookingDateStr);
      
      // Convert booking time to local date components for comparison
      // JavaScript Date automatically converts to local timezone
      const bookingLocalYear = bookingStart.getFullYear();
      const bookingLocalMonth = bookingStart.getMonth();
      const bookingLocalDate = bookingStart.getDate();
      const bookingLocalHour = bookingStart.getHours();
      const bookingLocalMinute = bookingStart.getMinutes();
      
      // Check if this booking is on the same local date as the slot
      const bookingDateOnlyStr = `${bookingLocalYear}-${String(bookingLocalMonth + 1).padStart(2, '0')}-${String(bookingLocalDate).padStart(2, '0')}`;
      
      // Only check bookings on the same local date
      if (bookingDateOnlyStr !== slotDateStr) continue;
      
      // Create a comparable local time object
      const bookingLocalStart = new Date(bookingLocalYear, bookingLocalMonth, bookingLocalDate, bookingLocalHour, bookingLocalMinute, 0, 0);
      const bookingDuration = booking.services?.duration_minutes || 60;
      const bookingLocalEnd = new Date(bookingLocalStart.getTime() + bookingDuration * 60000);
      
      console.log("[ProviderDetail] Conflict check:", {
        slotTime: timeStr,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
        bookingStart: bookingLocalStart.toISOString(),
        bookingEnd: bookingLocalEnd.toISOString(),
        practitionerId,
        bookingPractitionerId: booking.practitioner_id
      });
      
      // Check for overlap using local times
      if (slotStart < bookingLocalEnd && slotEnd > bookingLocalStart) {
        console.log("[ProviderDetail] Conflict found for slot:", timeStr);
        return false; // Conflict found
      }
    }
    
    return true;
  };

  // Generate time slots based on service duration and practitioner/business hours
  const generateTimeSlots = () => {
    if (selectedService === null || !selectedDate) {
      return [];
    }

    const service = services[selectedService];
    const duration = service?.duration_minutes || 60;
    const dayName = format(selectedDate, "EEEE");
    
    let openHour = 9, openMinute = 0, closeHour = 17, closeMinute = 0;
    
    // Use practitioner schedule if selected, otherwise business hours
    if (selectedPractitioner) {
      const practitioner = practitioners.find(p => p.id === selectedPractitioner);
      const practSchedule = practitioner?.availability_schedule?.[dayName];
      
      if (!practSchedule?.working) {
        return []; // Practitioner not working this day
      }
      
      const startParts = (practSchedule.start || "09:00").split(":");
      const endParts = (practSchedule.end || "17:00").split(":");
      openHour = Number(startParts[0]);
      openMinute = Number(startParts[1]) || 0;
      closeHour = Number(endParts[0]);
      closeMinute = Number(endParts[1]) || 0;
    } else {
      // Fallback to business hours when no practitioner selected
      const dayHours = business?.operating_hours?.[dayName];
      if (!dayHours || dayHours.closed) {
        return [];
      }
      const openParts = (dayHours.open || "09:00").split(":");
      const closeParts = (dayHours.close || "17:00").split(":");
      openHour = Number(openParts[0]);
      openMinute = Number(openParts[1]) || 0;
      closeHour = Number(closeParts[0]);
      closeMinute = Number(closeParts[1]) || 0;
    }

    const slots: string[] = [];
    let currentMinutes = openHour * 60 + openMinute;
    const endMinutes = closeHour * 60 + closeMinute;
    
    // Use 15-minute increments for all slots, regardless of service duration
    // This allows more flexible booking times while still checking duration fits
    while (currentMinutes + duration <= endMinutes) {
      const hour = Math.floor(currentMinutes / 60);
      const minute = currentMinutes % 60;
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const timeStr = `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
      slots.push(timeStr);
      currentMinutes += 15;
    }
    
    return slots;
  };

  // Check if a time slot is in the past
  const isSlotInPast = (date: Date, timeStr: string) => {
    const [time, period] = timeStr.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    let hour = hours;
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    
    const slotDateTime = new Date(date);
    slotDateTime.setHours(hour, minutes, 0, 0);
    
    return slotDateTime.getTime() < Date.now();
  };

  // Get all time slots with availability status for each
  const getTimeSlotsWithAvailability = () => {
    const allSlots = generateTimeSlots();
    if (!selectedPractitioner || !selectedDate) {
      return allSlots.map(slot => ({ 
        time: slot, 
        available: true,
        isPast: selectedDate ? isSlotInPast(selectedDate, slot) : false
      }));
    }
    
    return allSlots.map(slot => ({
      time: slot,
      available: isPractitionerAvailableAtTime(selectedPractitioner, selectedDate, slot),
      isPast: isSlotInPast(selectedDate, slot)
    }));
  };

  const handleServiceSelect = (index: number) => {
    setSelectedService(index);
    // Do not auto-select practitioner; customer must choose.
    setSelectedPractitioner(null);
    // Reset date/time selection when service changes
    setSelectedDate(undefined);
    setSelectedTime(null);
    setShowBookingSheet(false);
  };

  const handlePractitionerSelect = (practitionerId: string) => {
    setSelectedPractitioner(practitionerId);
  };

  const handleProceedToDateTime = () => {
    if (selectedService !== null && selectedPractitioner) {
      setShowBookingSheet(true);
    }
  };

  const handleBookNow = () => {
    if (selectedService !== null && selectedPractitioner) {
      setShowBookingSheet(true);
    }
  };

  // Called when user clicks "Confirm Booking" in the booking sheet
  const handleConfirmBookingClick = () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to book a service",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    if (selectedService === null || !selectedDate || !selectedTime || !business || !selectedPractitioner) return;

    // Show booking consent modal (APP-compliant)
    setShowBookingConsentModal(true);
  };

  // Called after user confirms in the booking consent modal
  const handleConfirmBooking = async () => {
    setShowBookingConsentModal(false);

    if (selectedService === null || !selectedDate || !selectedTime || !business || !selectedPractitioner || !user) return;

    setBookingLoading(true);
    
    try {
      const service = services[selectedService];
      
      // Parse the selected time and combine with date
      const [time, period] = selectedTime.split(" ");
      const [hours, minutes] = time.split(":");
      let hour = parseInt(hours);
      if (period === "PM" && hour !== 12) hour += 12;
      if (period === "AM" && hour === 12) hour = 0;
      
      const bookingDateTime = new Date(selectedDate);
      bookingDateTime.setHours(hour, parseInt(minutes), 0, 0);

      // Format as ISO string using BUSINESS timezone (not browser timezone)
      // This ensures the stored time represents the actual appointment time in the business's location
      const businessTimezone = business?.timezone || 'UTC';
      const year = bookingDateTime.getFullYear();
      const month = String(bookingDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(bookingDateTime.getDate()).padStart(2, '0');
      const hourStr = String(bookingDateTime.getHours()).padStart(2, '0');
      const minStr = String(bookingDateTime.getMinutes()).padStart(2, '0');
      // Get timezone offset for the business timezone (handles DST automatically)
      const tzOffset = getTimezoneOffset(bookingDateTime, businessTimezone);
      const bookingDateTimeISO = `${year}-${month}-${day}T${hourStr}:${minStr}:00${tzOffset}`;
      
      console.log("[ProviderDetail] Booking with business timezone:", {
        businessTimezone,
        localTime: `${year}-${month}-${day}T${hourStr}:${minStr}`,
        tzOffset,
        bookingDateTimeISO
      });

      // Get session token explicitly for the edge function call with retry for iOS Safari
      let session = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionData?.session?.access_token) {
          session = sessionData.session;
          break;
        }
        console.warn(`[ProviderDetail] Session attempt ${attempt + 1} failed:`, sessionError?.message);
        // Wait before retry (helps with iOS Safari session timing issues)
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (!session?.access_token) {
        console.error("[ProviderDetail] No session after retries", {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        });
        toast({
          title: "Session Expired",
          description: "Please log in again to continue booking.",
          variant: "destructive",
        });
        navigate('/auth');
        return;
      }

      // Use the create-booking edge function for server-side validation and conflict prevention
      console.log("[ProviderDetail] Calling create-booking edge function...");
      
      // Add timeout for mobile connections
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      try {
        const { data: response, error: fnError } = await supabase.functions.invoke("create-booking", {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: {
            customerId: user.id,
            businessId: business.id,
            serviceId: service.id,
            practitionerId: selectedPractitioner,
            bookingDateTime: bookingDateTimeISO,
          },
        });

        clearTimeout(timeoutId);

        console.log("[ProviderDetail] Edge function response:", { response, fnError });

        // Handle edge function errors - check both fnError and response.error
        if (fnError) {
          // fnError.message might contain the actual error from non-2xx responses
          const errorMsg = fnError.message || "Failed to create booking";
          console.error("[ProviderDetail] Edge function error:", {
            error: fnError,
            userAgent: navigator.userAgent,
            platform: navigator.platform
          });
          throw new Error(errorMsg);
        }

        // Check if response contains an error (edge function returned error JSON)
        if (response?.error) {
          console.error("[ProviderDetail] Booking error from response:", response.error);
          throw new Error(response.error);
        }

        if (!response?.success) {
          throw new Error("Booking failed - unexpected response");
        }

        const bookingData = response.booking;

        // Immediately update local state to mark this slot as booked
        setExistingBookings(prev => [...prev, {
          practitioner_id: selectedPractitioner,
          booking_date_time: bookingDateTimeISO,
          status: "confirmed",
          services: { duration_minutes: service.duration_minutes }
        }]);

        // Fetch customer profile for email
        const { data: profileData } = await supabase
          .from("profiles")
          .select("name, email")
          .eq("id", user.id)
          .single();

        // Send booking confirmation email with queue tracking
        if (profileData?.email) {
          const viewBookingUrl = `${window.location.origin}/customer/bookings`;
          const practitionerName = practitioners.find(p => p.id === selectedPractitioner)?.name || null;
          
          const emailPayload = {
            bookingId: bookingData.id,
            customerEmail: profileData.email,
            customerName: profileData.name || "Valued Customer",
            serviceName: service.title,
            serviceDuration: service.duration_minutes,
            businessName: business.business_name,
            businessAddress: [business.address, business.state, business.postcode].filter(Boolean).join(', '),
            businessContactNumber: business.contact_number,
            businessEmail: business.email,
            bookingDateTime: bookingDateTimeISO,
            amount: service.price,
            notes: null,
            viewBookingUrl,
            practitionerName,
          };

          // Insert email into queue for tracking
          let queueEntryId: string | null = null;
          try {
            const { data: queueEntry } = await supabase
              .from("email_queue")
              .insert({
                email_type: "booking_confirmation",
                recipient_email: profileData.email,
                recipient_name: profileData.name || "Valued Customer",
                payload: emailPayload,
                booking_id: bookingData.id,
                business_id: business.id,
                status: "pending",
              })
              .select("id")
              .single();
            
            queueEntryId = queueEntry?.id || null;
          } catch (queueError) {
            console.warn("Failed to create email queue entry:", queueError);
          }
          
          try {
            // Send booking confirmation email
            const { data: { session: emailSession } } = await supabase.auth.getSession();
            if (emailSession?.access_token) {
              const { error: invokeError } = await supabase.functions.invoke("send-booking-confirmation", {
                headers: {
                  Authorization: `Bearer ${emailSession.access_token}`,
                },
                body: emailPayload,
              });
            
              if (invokeError) {
                console.warn("Booking confirmation email failed:", invokeError.message);
                // Update queue status to failed
                if (queueEntryId) {
                  await supabase.from("email_queue")
                    .update({ 
                      status: "failed", 
                      last_error: invokeError.message,
                      attempts: 1,
                      next_retry_at: new Date(Date.now() + 60000).toISOString() // Retry in 1 min
                    })
                    .eq("id", queueEntryId);
                }
              } else {
                console.log("Booking confirmation email sent successfully");
                // Update queue status to sent
                if (queueEntryId) {
                  await supabase.from("email_queue")
                    .update({ 
                      status: "sent", 
                      sent_at: new Date().toISOString(),
                      attempts: 1 
                    })
                    .eq("id", queueEntryId);
                }
              }
            }
          } catch (emailError: any) {
            // Don't fail the booking if email fails
            console.warn("Failed to send confirmation email:", emailError);
            // Update queue status to failed
            if (queueEntryId) {
              await supabase.from("email_queue")
                .update({ 
                  status: "failed", 
                  last_error: emailError?.message || "Unknown error",
                  attempts: 1,
                  next_retry_at: new Date(Date.now() + 60000).toISOString()
                })
                .eq("id", queueEntryId);
            }
          }
        }

        toast({
          title: "Booking Confirmed!",
          description: "A confirmation email has been sent to your email address.",
        });

        // Close the sheet and redirect to checkout
        setShowBookingSheet(false);
        navigate(`/booking/checkout?bookingId=${bookingData.id}`);
      } catch (innerError: any) {
        clearTimeout(timeoutId);
        throw innerError; // Re-throw to outer catch
      }
    } catch (error: any) {
      console.error("Booking error:", error);
      
      // Handle specific error messages from edge function
      let errorMessage = error.message || "Failed to create booking";
      if (errorMessage.includes("not available at this time")) {
        errorMessage = "This time slot is no longer available. Please select a different time.";
        // Clear the invalid selection and refresh bookings
        setSelectedTime(null);
        fetchBusinessData();
      } else if (errorMessage.includes("Rate limit")) {
        errorMessage = "Too many booking attempts. Please wait a moment and try again.";
      }
      
      toast({
        title: "Booking Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-16 md:pt-20 pb-24 lg:pb-8 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-16 md:pt-20 pb-24 lg:pb-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Business Not Found</h1>
            <p className="text-muted-foreground mb-4">The business you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/browse">Browse Businesses</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length 
    : undefined;
 
  // Generate structured data for this business
  const businessSchema = generateLocalBusinessSchema({
    id: business.id,
    name: business.business_name,
    description: business.description || undefined,
    address: business.address,
    phone: business.contact_number || undefined,
    email: business.email || undefined,
    category: business.category,
    operatingHours: business.operating_hours,
    rating: averageRating,
    reviewCount: reviews.length,
  });

  const isPractitionerRequired = practitioners.length > 0;
  const isDateTimeDisabled =
    selectedService === null || (isPractitionerRequired && !selectedPractitioner);

  const dateTimeCtaText =
    selectedService === null
      ? "Select a Service First"
      : isPractitionerRequired && !selectedPractitioner
        ? "Choose a Practitioner"
        : "Select Date & Time";

  const DateTimeButton = ({ className = "" }: { className?: string }) => (
    <Button
      variant="hero"
      className={cn("w-full relative z-50 pointer-events-auto", className)}
      size="lg"
      type="button"
      disabled={isDateTimeDisabled}
      onClick={(e) => {
        e.stopPropagation();
        setShowBookingSheet(true);
      }}
    >
      <CalendarIcon className="w-5 h-5 mr-2" />
      {dateTimeCtaText}
    </Button>
  );

  return (

    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${business.business_name} - ${formatCategory(business.category)} in ${business.address.split(',').pop()?.trim() || 'your area'}`}
        description={business.description || `Book services at ${business.business_name}, a ${formatCategory(business.category)} in ${business.address}. View prices, read reviews, and book instantly with debit or credit card.`}
        keywords={`${business.business_name}, ${formatCategory(business.category)}, wellness ${business.address.split(',').pop()?.trim()}, spa booking, massage appointment`}
        canonicalUrl={`/provider/${business.id}`}
        ogType="business.business"
        structuredData={businessSchema}
      />
      <Navbar />
      
      <main className="pt-16 md:pt-20 pb-8">
        {/* Image Gallery */}
        <div className="relative h-[35vh] md:h-[45vh] lg:h-[50vh] overflow-hidden bg-muted">
          {images.length > 0 ? (
            <img
              src={images[currentImageIndex]}
              alt={business.business_name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-wellness-teal/20">
              <Building2 className="w-24 h-24 text-primary/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-foreground/20" />
          
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-10 md:h-10 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-colors touch-manipulation"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-10 md:h-10 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-colors touch-manipulation"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Dots */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`h-2 rounded-full transition-all touch-manipulation ${
                    index === currentImageIndex ? "bg-primary w-6" : "bg-card/60 w-2"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Back button */}
          <Link
            to="/browse"
            className="absolute top-4 left-4 w-12 h-12 md:w-auto md:h-auto md:px-4 md:py-2 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center md:gap-2 text-sm font-medium hover:bg-card transition-colors touch-manipulation"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden md:inline">Back</span>
          </Link>

          {/* Actions */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button className="w-12 h-12 md:w-10 md:h-10 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-colors touch-manipulation">
              <Share2 className="w-5 h-5" />
            </button>
            <button className="w-12 h-12 md:w-10 md:h-10 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-colors touch-manipulation">
              <Heart className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge>{formatCategory(business.category)}</Badge>
                  {reviews.length > 0 && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">
                        {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)}
                      </span>
                      <span className="text-muted-foreground">({reviews.length} reviews)</span>
                    </div>
                  )}
                </div>
                <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3">
                  {business.business_name}
                </h1>
                <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span>{business.address}</span>
                </div>
              </div>

              {/* Quick Info - Mobile (Hidden on mobile/tablet as per requirements) */}
              {/* Call, Website, and Directions buttons removed for mobile/tablet */}

              {/* About */}
              {business.description && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-3">About</h2>
                  <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                    {business.description}
                  </p>
                </Card>
              )}

              {/* Services */}
              {services.length > 0 && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-4">
                    Step 1: Select a Service
                  </h2>
                  <div className="space-y-3">
                    {services.map((service, index) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceSelect(index)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all touch-manipulation active:scale-[0.98] ${
                          selectedService === index
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground">{service.title}</h3>
                              {selectedService === index && (
                                <Check className="w-5 h-5 text-primary shrink-0" />
                              )}
                            </div>
                            {service.description && (
                              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{service.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">{service.duration_minutes} min</p>
                          </div>
                        <p className="font-display text-lg font-semibold text-primary shrink-0">
                            ${service.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* Practitioner Selection - Show after service is selected */}
              {selectedService !== null && practitioners.length > 0 && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-4">
                    Step 2: Choose Your Practitioner
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">Select a practitioner for your service</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {practitioners.map((practitioner) => (
                      <button
                        key={practitioner.id}
                        onClick={() => handlePractitionerSelect(practitioner.id)}
                        className={`p-4 rounded-xl border-2 text-center transition-all touch-manipulation active:scale-[0.98] ${
                          selectedPractitioner === practitioner.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        {practitioner.photo ? (
                          <img
                            src={practitioner.photo}
                            alt={practitioner.name}
                            loading="lazy"
                            decoding="async"
                            className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-primary/20"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto mb-2 flex items-center justify-center">
                            <span className="text-2xl font-semibold text-primary">
                              {practitioner.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <h3 className="font-semibold text-foreground text-sm">{practitioner.name}</h3>
                        {practitioner.specialization && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{practitioner.specialization}</p>
                        )}
                        {selectedPractitioner === practitioner.id && (
                          <Check className="w-5 h-5 text-primary mx-auto mt-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* Team Section - Show all practitioners */}
              {practitioners.length > 0 && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-4">Our Team</h2>
                  <div className="flex gap-4 overflow-x-auto scrollbar-none -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3">
                    {practitioners.map((practitioner) => (
                      <div key={practitioner.id} className="text-center shrink-0 w-28 md:w-auto">
                        {practitioner.photo ? (
                          <img
                            src={practitioner.photo}
                            alt={practitioner.name}
                            loading="lazy"
                            decoding="async"
                            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover mx-auto mb-2 border-2 border-primary/20"
                          />
                        ) : (
                          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mx-auto mb-2 flex items-center justify-center">
                            <span className="text-2xl font-semibold text-primary">
                              {practitioner.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <h3 className="font-semibold text-foreground text-sm">{practitioner.name}</h3>
                        {practitioner.specialization && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{practitioner.specialization}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Book Now Card - Mobile/Tablet (moved after Our Team section) */}
              <Card variant="elevated" className="p-4 md:p-6 lg:hidden">
                <h2 className="font-display text-xl font-semibold text-foreground mb-4">Book Now</h2>
                
                {/* Selected Service Summary */}
                {selectedService !== null && services[selectedService] ? (
                  <div className="bg-primary/5 rounded-xl p-4 mb-4">
                    <p className="font-medium text-foreground">{services[selectedService].title}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-muted-foreground">{services[selectedService].duration_minutes} min</span>
                      <span className="font-display font-semibold text-primary">
                        ${services[selectedService].price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">Step 1: Select a service above</p>
                )}

                {/* Selected Practitioner Summary */}
                {selectedPractitioner && selectedService !== null && (
                  <div className="bg-secondary rounded-xl p-4 mb-4">
                    <p className="text-xs text-muted-foreground mb-1">Practitioner</p>
                    <p className="font-medium text-foreground">
                      {practitioners.find(p => p.id === selectedPractitioner)?.name}
                    </p>
                  </div>
                )}

                {selectedService !== null && !selectedPractitioner && getTodayAvailablePractitioners().length > 0 && (
                  <p className="text-sm text-muted-foreground mb-4">Step 2: Choose a practitioner above</p>
                )}

                <div className="space-y-4">
                  {/* Operating Hours */}
                  {business.operating_hours && Object.keys(business.operating_hours).length > 0 && (
                    <div className="pb-3 border-b border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-foreground">Operating Hours</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
                          const hours = business.operating_hours?.[day];
                          if (!hours) return null;
                          return (
                            <div key={day} className="flex justify-between text-muted-foreground">
                              <span>{day.slice(0, 3)}</span>
                              <span>{hours.closed ? "Closed" : `${hours.open} - ${hours.close}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Contact Details */}
                  <div className="space-y-3 pb-3 border-b border-border">
                    {business.contact_number && (
                      <a 
                        href={`sms:${business.contact_number}`}
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                      >
                        <Phone className="w-4 h-4" />
                        {business.contact_number}
                      </a>
                    )}
                    {business.email && (
                      <a 
                        href={`mailto:${business.email}`}
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        {business.email}
                      </a>
                    )}
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {business.address}
                    </div>
                  </div>
                  
                  {/* Date/Time Button */}
                  <div className="pt-2">
                    <DateTimeButton />
                  </div>
                </div>
              </Card>

              {/* Products / Inventory */}
              {inventory.length > 0 && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-4">
                    <Package className="w-5 h-5 inline-block mr-2 text-primary" />
                    Products Available
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {inventory.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-xl border border-border overflow-hidden"
                      >
                        {item.product_photo ? (
                          <img
                            src={item.product_photo}
                            alt={item.product_name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-28 object-cover"
                          />
                        ) : (
                          <div className="w-full h-28 bg-muted flex items-center justify-center">
                            <Package className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="p-3">
                          <h3 className="font-semibold text-foreground text-sm line-clamp-1">{item.product_name}</h3>
                          {item.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.description}</p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="font-display font-semibold text-primary">
                              ${Number(item.price).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="secondary" className="mt-2 text-xs">
                            {item.stock_qty} in stock
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Customer Reviews Section */}
              {reviews.length > 0 && (
                <Card variant="elevated" className="p-4 md:p-6">
                  <h2 className="font-display text-lg md:text-xl font-semibold text-foreground mb-4">
                    <Star className="w-5 h-5 inline-block mr-2 text-yellow-400 fill-yellow-400" />
                    Customer Reviews ({reviews.length})
                  </h2>
                  <div className="space-y-4">
                    {reviews.slice(0, 5).map((review, index) => (
                      <div key={index} className="border-b border-border pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(new Date(review.created_at), "MMM d, yyyy")}
                          </span>
                        </div>
                        {review.profiles?.name && (
                          <p className="text-sm font-medium text-foreground mb-1">{review.profiles.name}</p>
                        )}
                        {review.comment && (
                          <p className="text-sm text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Google AdSense After Customer Reviews */}
              <AdSenseUnit className="my-4" />

            </div>

            {/* Sidebar - Booking Card (Desktop only) */}
            <div className="hidden lg:block lg:col-span-1">
              <Card variant="elevated" className="p-6 sticky top-24">
                <h2 className="font-display text-xl font-semibold text-foreground mb-4">Book Now</h2>
                
                {/* Selected Service Summary */}
                {selectedService !== null && services[selectedService] ? (
                  <div className="bg-primary/5 rounded-xl p-4 mb-4">
                    <p className="font-medium text-foreground">{services[selectedService].title}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-muted-foreground">{services[selectedService].duration_minutes} min</span>
                      <span className="font-display font-semibold text-primary">
                        ${services[selectedService].price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">Step 1: Select a service to book</p>
                )}

                {/* Selected Practitioner Summary */}
                {selectedPractitioner && selectedService !== null && (
                  <div className="bg-secondary rounded-xl p-4 mb-4">
                    <p className="text-xs text-muted-foreground mb-1">Practitioner</p>
                    <p className="font-medium text-foreground">
                      {practitioners.find(p => p.id === selectedPractitioner)?.name}
                    </p>
                  </div>
                )}

                {selectedService !== null && !selectedPractitioner && practitioners.length > 0 && (
                  <p className="text-sm text-muted-foreground mb-4">Step 2: Choose a practitioner</p>
                )}

                <div className="space-y-4">
                  {/* Operating Hours */}
                  {business.operating_hours && Object.keys(business.operating_hours).length > 0 && (
                    <div className="pb-3 border-b border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-foreground">Operating Hours</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
                          const hours = business.operating_hours?.[day];
                          if (!hours) return null;
                          return (
                            <div key={day} className="flex justify-between text-muted-foreground">
                              <span>{day.slice(0, 3)}</span>
                              <span>{hours.closed ? "Closed" : `${hours.open} - ${hours.close}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Contact Details */}
                  <div className="space-y-3 pb-3 border-b border-border">
                    {business.contact_number && (
                      <a 
                        href={`sms:${business.contact_number}`}
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                      >
                        <Phone className="w-4 h-4" />
                        {business.contact_number}
                      </a>
                    )}
                    {business.email && (
                      <a 
                        href={`mailto:${business.email}`}
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        {business.email}
                      </a>
                    )}
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {business.address}
                    </div>
                  </div>
                  
                  {/* Date/Time Button */}
                  <div className="pt-2">
                    <DateTimeButton />
                  </div>
                </div>
              </Card>
              
              {/* Google AdSense Below Book Now */}
              <AdSenseUnit className="mt-4" />
            </div>
          </div>
        </div>
      </main>

      {/* Booking Sheet */}
      <Sheet open={showBookingSheet} onOpenChange={setShowBookingSheet}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl flex flex-col">
          <SheetHeader className="text-left pb-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-display text-xl">
                Complete Booking
              </SheetTitle>
              <button 
                onClick={() => setShowBookingSheet(false)}
                className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center touch-manipulation"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </SheetHeader>
          
          <div className="py-6 overflow-y-auto flex-1">
            {selectedService !== null && services[selectedService] && (
              <div className="bg-secondary rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-foreground">{services[selectedService].title}</h3>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-muted-foreground">{services[selectedService].duration_minutes} min</span>
                  <span className="font-display font-semibold text-primary">
                    ${services[selectedService].price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Date Selection - Only show if not yet selected */}
              {!selectedDate ? (
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Select Date</label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    className="p-3 pointer-events-auto rounded-lg border border-border"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Selected Date</label>
                  <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-primary" />
                      <span className="font-medium">{format(selectedDate, "PPP")}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedDate(undefined);
                        setSelectedTime(null);
                      }}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              )}

              {/* Practitioner Availability Warning */}
              {selectedDate && selectedPractitioner && !isPractitionerWorkingOnDay(
                practitioners.find(p => p.id === selectedPractitioner)!,
                selectedDate
              ) && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-center">
                  <p className="text-destructive font-medium">
                    {practitioners.find(p => p.id === selectedPractitioner)?.name} is not available on this day.
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Please select a different date or change the practitioner.</p>
                </div>
              )}

              {/* Time Selection - Only show after date is selected and practitioner is available */}
              {selectedDate && (
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Select Time</label>
                  {selectedPractitioner && !isPractitionerWorkingOnDay(
                    practitioners.find(p => p.id === selectedPractitioner)!,
                    selectedDate
                  ) ? (
                    <div className="p-4 bg-muted rounded-lg text-center text-muted-foreground">
                      <p>No time slots available.</p>
                      <p className="text-sm mt-1">The selected practitioner is not working on this day.</p>
                    </div>
                  ) : getTimeSlotsWithAvailability().length === 0 ? (
                    <div className="p-4 bg-muted rounded-lg text-center text-muted-foreground">
                      <p>No available time slots for this day.</p>
                      <p className="text-sm mt-1">Please select a different date.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                      {getTimeSlotsWithAvailability().map(({ time, available, isPast }) => {
                        const isDisabled = !available || isPast;
                        return (
                          <Button 
                            key={time} 
                            variant={selectedTime === time ? "default" : "outline"} 
                            className={cn(
                              "h-11 text-sm touch-manipulation",
                              isDisabled && "opacity-50 cursor-not-allowed line-through bg-muted text-muted-foreground hover:bg-muted"
                            )}
                            onClick={() => !isDisabled && setSelectedTime(time)}
                            disabled={isDisabled}
                          >
                            {time}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-8">
              {/* Show warning if practitioner not selected but required */}
              {practitioners.length > 0 && !selectedPractitioner && (
                <p className="text-sm text-destructive text-center mb-3">
                  Please select a practitioner to continue
                </p>
              )}
              <Button 
                variant="hero" 
                size="lg" 
                className="w-full touch-manipulation"
                disabled={!selectedDate || !selectedTime || (practitioners.length > 0 && !selectedPractitioner) || bookingLoading}
                onClick={handleConfirmBookingClick}
              >
                {bookingLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  "Confirm Booking"
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Booking Consent Modal (APP-compliant) */}
      <BookingConsentModal
        open={showBookingConsentModal}
        onOpenChange={setShowBookingConsentModal}
        onConfirm={handleConfirmBooking}
        businessName={business?.business_name || ""}
        serviceName={selectedService !== null ? services[selectedService]?.title || "" : ""}
        isSubmitting={bookingLoading}
      />

      <Footer />
    </div>
  );
}
